import { type FC, useMemo, useCallback } from "react";

import { Select } from "@/components/flexkit/select/Select";
import { toast } from "@/components/flexkit/toast";
import { formatSeconds } from "@/format/time";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { isOrgPolicyMoreRestrictive } from "@/utils/auto-delete";
import { formatError } from "@/utils/errors";

type AutoDeleteOptionWithDisabled = {
    label: string;
    value: number;
    disabled?: boolean;
};

const AUTO_DELETE_ARCHIVED_ENVIRONMENTS_AFTER = "DELETE_ARCHIVED_ENVIRONMENTS_AFTER";

const AUTO_DELETE_BASE_OPTIONS: { label: string; value: number }[] = [
    { label: "1 day", value: 1 * 24 * 60 * 60 },
    { label: "2 days", value: 2 * 24 * 60 * 60 },
    { label: "3 days", value: 3 * 24 * 60 * 60 },
    { label: "5 days", value: 5 * 24 * 60 * 60 },
    { label: "1 week", value: 7 * 24 * 60 * 60 },
    { label: "2 weeks", value: 14 * 24 * 60 * 60 },
    { label: "4 weeks", value: 4 * 7 * 24 * 60 * 60 },
    { label: "never", value: 0 },
];

export const AutoDeleteSelect: FC = () => {
    const { value: userPreferenceRaw, setValueAsync: setUserPreferenceAutoDelete } = usePreferenceValue(
        AUTO_DELETE_ARCHIVED_ENVIRONMENTS_AFTER,
    );
    const { data: organizationPolicies, isLoading: isPoliciesLoading } = useGetOrganizationPolicies();

    // Get organization policy value in seconds
    const organizationPolicySeconds = useMemo(() => {
        const seconds = organizationPolicies?.deleteArchivedEnvironmentsAfter?.seconds;
        return typeof seconds === "bigint" ? Number(seconds) : undefined;
    }, [organizationPolicies]);

    // Check if the currently selected option is enforced by org policy and determine effective selection
    const { isOrgPolicyEnforced, effectiveSelectedOption } = useMemo(() => {
        const userPreference = typeof userPreferenceRaw === "string" ? parseInt(userPreferenceRaw, 10) || 0 : 0;

        if (!organizationPolicySeconds) {
            return {
                isOrgPolicyEnforced: false,
                effectiveSelectedOption: userPreference,
            };
        }

        const isEnforced = isOrgPolicyMoreRestrictive(organizationPolicySeconds, userPreference);

        return {
            isOrgPolicyEnforced: isEnforced,
            effectiveSelectedOption: isEnforced ? organizationPolicySeconds : userPreference,
        };
    }, [userPreferenceRaw, organizationPolicySeconds]);

    // Filter options based on organization policy
    const availableOptions = useMemo((): AutoDeleteOptionWithDisabled[] => {
        if (!organizationPolicySeconds) {
            // If no org policy, all base options are available
            let options = AUTO_DELETE_BASE_OPTIONS.map((option) => ({
                ...option,
                disabled: false,
            }));

            // Add custom option only if it's the selected value and not in base options
            if (!options.some((option) => option.value === effectiveSelectedOption)) {
                const formattedTime = formatSeconds(effectiveSelectedOption, "long");
                const label = `after ${formattedTime}`;

                options = [
                    ...options,
                    {
                        label,
                        value: effectiveSelectedOption,
                        disabled: false,
                    },
                ];
            }

            return options;
        }

        const baseOptions = AUTO_DELETE_BASE_OPTIONS.map((option) => ({
            ...option,
            disabled: option.value > organizationPolicySeconds || option.value === 0, // "Never" (0) is disabled when org policy exists
        }));

        // Add the effective selected option if it's not in the standard options
        if (!baseOptions.some((option) => option.value === effectiveSelectedOption)) {
            const formattedTime = formatSeconds(effectiveSelectedOption, "long");
            const label = `After ${formattedTime}`;

            return [
                ...baseOptions,
                {
                    label,
                    value: effectiveSelectedOption,
                    disabled: effectiveSelectedOption > organizationPolicySeconds,
                },
            ];
        }

        return baseOptions;
    }, [organizationPolicySeconds, effectiveSelectedOption]);

    // Memoized display text for the selected option
    const selectedOptionDisplay = useMemo(() => {
        const option = availableOptions.find((opt) => opt.value === effectiveSelectedOption);
        if (!option?.label) return "";

        // Special case for "never" option
        if (option.value === 0) {
            return "Auto-delete never";
        }

        return `Auto-delete after ${option.label}`;
    }, [availableOptions, effectiveSelectedOption]);

    const handleOptionChange = useCallback(
        async (value: string) => {
            const newOption = parseInt(value, 10);

            // If clicking the org policy value and it's enforced, do nothing
            if (isOrgPolicyEnforced && newOption === organizationPolicySeconds) {
                return;
            }

            try {
                await setUserPreferenceAutoDelete(newOption.toString());

                // Get the selected option label for the toast message
                const selectedOption = availableOptions.find((opt) => opt.value === newOption);
                const description =
                    newOption === 0
                        ? "Your archived environments will no longer be deleted automatically!"
                        : `Your archived environments will be automatically deleted after ${selectedOption?.label}!`;

                toast({
                    title: "Auto-delete preference updated",
                    description,
                });
            } catch (error) {
                toast({
                    title: "Failed to update preference",
                    description: formatError(error),
                });
            }
        },
        [isOrgPolicyEnforced, organizationPolicySeconds, setUserPreferenceAutoDelete, availableOptions],
    );

    return (
        <Select
            name="auto-delete-archived-environment"
            value={effectiveSelectedOption.toString()}
            onValueChange={handleOptionChange}
            loading={isPoliciesLoading}
            className="w-full"
            data-testid="auto-delete-archived-environment-select"
            formatDisplayValue={() => <span>{selectedOptionDisplay}</span>}
        >
            {availableOptions.map((option) => (
                <Select.Item key={option.value} value={option.value.toString()} disabled={option.disabled}>
                    {option.value === 0 ? "Never" : `After ${option.label}`}
                </Select.Item>
            ))}
        </Select>
    );
};
